import { useState, useEffect, Suspense } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SubscriptionGate from "@/components/SubscriptionGate";
import HelpModal from "@/components/HelpModal";
import NotificationBell from "@/components/NotificationBell";
import UpdateBanner from "@/components/UpdateBanner";
import RouteFallback from "@/components/RouteFallback";
import {
  SquaresFour, Users, Wrench, CalendarBlank, Gear, SignOut, Calculator, CloudSun, Receipt, Crown,
  Plus, X, FileText, DotsThreeOutline, Question, Clock, List,
} from "@phosphor-icons/react";

// Full navigation — shown in the slide-out drawer on every breakpoint.
const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/schedule", label: "Schedule", icon: CalendarBlank },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/invoices", label: "Invoices/Quotes", icon: Receipt },
  { to: "/timesheets", label: "Timesheets", icon: Clock },
  { to: "/weather", label: "Weather", icon: CloudSun },
  { to: "/tools", label: "Tools", icon: Calculator },
  { to: "/settings", label: "Settings", icon: Gear },
];

// Mobile bottom nav — primary tabs + a "More" sheet
const BOTTOM_NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/schedule", label: "Schedule", icon: CalendarBlank },
  { to: "/clients", label: "Clients", icon: Users },
];

const MORE_ITEMS = [
  { to: "/invoices", label: "Invoices/Quotes", icon: Receipt },
  { to: "/timesheets", label: "Timesheets", icon: Clock },
  { to: "/weather", label: "Weather", icon: CloudSun },
  { to: "/tools", label: "Tools", icon: Calculator },
  { to: "/settings", label: "Settings", icon: Gear },
];

const FAB_ACTIONS = [
  { label: "Add Job", to: "/jobs", state: { openNew: true }, icon: Wrench },
  { label: "Add Client", to: "/clients", state: { openNew: true }, icon: Users },
  { label: "Add Quote", to: "/invoices", state: { openNew: "quote" }, icon: FileText },
  { label: "Add Invoice", to: "/invoices", state: { openNew: "invoice" }, icon: Receipt },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const teamRole = user?.team_role || "owner";
  const isCrew = teamRole === "crew";
  const canInvoice = !isCrew || !!user?.can_invoice;
  const hidden = [
    ...(isCrew ? ["/invoices", "/tools"] : []),
    ...(canInvoice ? [] : ["/to-be-invoiced"]),
  ];
  const nav = NAV.filter((n) => !hidden.includes(n.to));
  const bottomNav = BOTTOM_NAV.filter((n) => !hidden.includes(n.to));
  const moreItems = MORE_ITEMS.filter((n) => !hidden.includes(n.to));
  const navigate = useNavigate();
  const location = useLocation();
  const [fabOpen, setFabOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the slide-out menu whenever the route changes.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Crew can't reach invoices/tools — bounce direct URL access back to the dashboard.
  useEffect(() => {
    if (isCrew && hidden.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
      navigate("/", { replace: true });
    }
  }, [isCrew, location.pathname, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (user?.access?.locked && location.pathname !== "/settings") return <SubscriptionGate />;

  const access = user?.access || {};
  const trialDaysLeft = () => {
    if (!access.trial_ends_at) return null;
    const ms = new Date(access.trial_ends_at) - new Date();
    const days = Math.ceil(ms / 86400000);
    return days > 0 ? days : 0;
  };

  const goFab = (to, state) => { setFabOpen(false); navigate(to, state ? { state } : undefined); };

  return (
    <div className="min-h-screen bg-background overflow-x-clip">
      {/* Unified top header — hamburger (left), centered logo, empty right. All breakpoints. */}
      <header className="sticky top-0 z-40 bg-[#2E2E2E] text-white h-14 flex items-center px-2 sm:px-4">
        <button onClick={() => setMenuOpen(true)} data-testid="menu-button" aria-label="Open menu"
          className="p-2 rounded-lg hover:bg-white/10 transition-colors shrink-0 flex items-center gap-1.5">
          <List size={26} weight="bold" />
          <span className="font-semibold text-sm">Menu</span>
        </button>
        <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
          <img src="/icon.png" alt="MowGuru" className="w-8 h-8 rounded-[8px] bg-white object-contain shrink-0" />
          <span className="font-display font-black text-lg tracking-tight truncate">MowGuru</span>
        </div>
        {/* Right spacer keeps the logo optically centered; intentionally empty. */}
        <div className="w-10 shrink-0" aria-hidden="true" />
      </header>

      {/* Slide-out navigation drawer (all breakpoints) */}
      {menuOpen && (
        <div className="fixed inset-0 z-[70]" data-testid="app-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} data-testid="drawer-backdrop" />
          <div className="absolute top-0 left-0 h-full w-72 max-w-[85vw] bg-[#2E2E2E] text-white flex flex-col shadow-[2px_0_16px_rgba(0,0,0,0.3)]">
            <div className="px-5 py-4 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2 min-w-0">
                <img src="/icon.png" alt="MowGuru" className="w-9 h-9 rounded-[10px] bg-white object-contain shrink-0" />
                <span className="font-display font-black text-lg truncate">MowGuru</span>
              </div>
              <button onClick={() => setMenuOpen(false)} data-testid="drawer-close" aria-label="Close menu"
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"><X size={22} weight="bold" /></button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {nav.map(({ to, label, icon: Icon, end }) => (
                <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase()}`}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-[10px] text-[15px] font-semibold transition-colors ${
                      isActive ? "bg-[#3BB54A] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`}>
                  <Icon size={22} weight="regular" />
                  {label}
                </NavLink>
              ))}

              <div className="my-3 border-t border-white/10" />

              {/* Notifications — full-width row; panel opens as an overlay above the drawer */}
              <div data-testid="drawer-notifications">
                <NotificationBell variant="drawer" />
              </div>
              {/* Help */}
              <button onClick={() => { setMenuOpen(false); setHelpOpen(true); }} data-testid="help-button-menu"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-[10px] text-[15px] font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                <Question size={22} weight="bold" /> Help
              </button>
            </nav>

            <div className="px-4 py-4 border-t border-white/10">
              <div className="mb-3">
                <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
                <p className="text-xs text-white/50 capitalize">{teamRole === "owner" ? (user?.subscription_tier || "free") : teamRole}</p>
              </div>
              <button onClick={handleLogout} data-testid="logout-button"
                className="w-full flex items-center gap-2 px-4 py-3 rounded-[10px] text-sm font-semibold bg-white/5 hover:bg-[#E63946] transition-colors">
                <SignOut size={20} weight="regular" /> Log out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto w-full overflow-x-clip pb-24 md:pb-10">
        <UpdateBanner />
        {access.status === "trialing" && (
          <div data-testid="trial-banner"
            className="mb-5 rounded-[12px] bg-[#EAF7EC] border border-[#3BB54A]/50 px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[#2E2E2E] flex items-center gap-2">
              <Crown size={18} weight="fill" className="text-[#F28C28]" />
              Pro trial active — {trialDaysLeft()} day{trialDaysLeft() === 1 ? "" : "s"} left. Unlock everything after it ends.
            </p>
            <NavLink to="/settings" data-testid="trial-banner-upgrade"
              className="text-sm font-bold text-[#3BB54A] underline shrink-0 hover:text-[#2E9E3F]">Upgrade</NavLink>
          </div>
        )}
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Mobile FAB + quick-add menu (hidden for crew — they can't create) */}
      {!isCrew && (
      <div className="md:hidden">
        {fabOpen && (
          <div className="fixed inset-0 z-40 bg-black/30" data-testid="fab-backdrop" onClick={() => setFabOpen(false)} />
        )}
        <div className="fixed bottom-24 right-5 z-50 flex flex-col items-end gap-3">
          {fabOpen && FAB_ACTIONS.map(({ label, to, state, icon: Icon }) => (
            <button key={label} onClick={() => goFab(to, state)} data-testid={`fab-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-2 pl-4 pr-3 py-2.5 rounded-full bg-white text-[#2E2E2E] font-semibold text-sm shadow-[0px_2px_8px_rgba(0,0,0,0.12)] animate-fade-up">
              {label} <span className="w-8 h-8 rounded-full bg-[#EAF7EC] flex items-center justify-center"><Icon size={16} weight="regular" className="text-[#3BB54A]" /></span>
            </button>
          ))}
          <button onClick={() => setFabOpen((o) => !o)} data-testid="fab-button" aria-label="Quick add"
            className="w-14 h-14 rounded-full bg-[#3BB54A] text-white flex items-center justify-center shadow-[0px_2px_8px_rgba(0,0,0,0.18)] hover:bg-[#2E9E3F] hover:scale-105 transition-transform">
            {fabOpen ? <X size={26} weight="bold" /> : <Plus size={28} weight="bold" />}
          </button>
        </div>
      </div>
      )}

      {/* Mobile bottom nav — white, simple line icons */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#EAEAEA] flex shadow-[0px_-2px_8px_rgba(0,0,0,0.06)]" aria-label="Primary">
        {bottomNav.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`mobile-nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-semibold transition-colors ${
                isActive ? "text-[#3BB54A]" : "text-[#6F6F6F]"
              }`}>
            {({ isActive }) => (
              <>
                <Icon size={24} weight={isActive ? "fill" : "regular"} />
                {label}
              </>
            )}
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(true)} data-testid="mobile-nav-more"
          className={`flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-semibold transition-colors ${
            moreItems.some((m) => location.pathname === m.to) ? "text-[#3BB54A]" : "text-[#6F6F6F]"
          }`}>
          <DotsThreeOutline size={24} weight={moreItems.some((m) => location.pathname === m.to) ? "fill" : "regular"} />
          More
        </button>
      </nav>

      {/* Mobile "More" sheet */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="more-sheet">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMoreOpen(false)} data-testid="more-backdrop" />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[16px] p-6 pb-8 shadow-[0px_-2px_12px_rgba(0,0,0,0.12)] animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg text-[#2E2E2E]">More</h3>
              <button onClick={() => setMoreOpen(false)} data-testid="more-close" className="p-1 text-[#6F6F6F]"><X size={22} weight="bold" /></button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {moreItems.map(({ to, label, icon: Icon }) => (
                <button key={to} onClick={() => { setMoreOpen(false); navigate(to); }}
                  data-testid={`more-nav-${label.toLowerCase()}`}
                  className="flex flex-col items-center gap-2 py-3 rounded-[12px] hover:bg-[#EAF7EC] transition-colors">
                  <span className="w-12 h-12 rounded-full bg-[#EAF7EC] flex items-center justify-center">
                    <Icon size={22} weight="regular" className="text-[#3BB54A]" />
                  </span>
                  <span className="text-xs font-semibold text-[#2E2E2E]">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
